import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { SmilePlus } from 'lucide-react';
import type { CommentReactionsMap } from '../types';
import './CommentReactions.css';

const PICKER_EMOJIS = ['👍', '👎', '❤️', '😄', '😮', '🎉', '✅', '👀', '🔥', '💯'];

/** Ignore pill toggles briefly after a picker select so a follow-up click cannot undo the save. */
const POST_PICK_SUPPRESS_MS = 450;

export interface CommentReactionsProps {
  reactions: CommentReactionsMap;
  currentUserId?: string;
  onToggle: (emoji: string) => void | Promise<unknown>;
  getUserDisplayName?: (userId: string) => string;
  classPrefix?: string;
  /**
   * When true (default): keep the add-reaction control in the comment layout at all times
   * so hover cannot change card height. Existing reactions stay visible either way.
   */
  revealOnHover?: boolean;
}

export const CommentReactions: React.FC<CommentReactionsProps> = ({
  reactions,
  currentUserId,
  onToggle,
  getUserDisplayName = (id) => id.slice(0, 8),
  classPrefix = '',
  revealOnHover = true,
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerFixedPos, setPickerFixedPos] = useState<{ top: number; left: number } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const pickerPortalRef = useRef<HTMLDivElement>(null);
  const suppressPillUntilRef = useRef(0);
  const inFlightRef = useRef<Set<string>>(new Set());
  const base = classPrefix ? `${classPrefix}comment-reactions` : 'comment-reactions';

  const runToggle = useCallback(
    async (emoji: string) => {
      const key = emoji.trim();
      if (!key || !currentUserId) return;
      if (inFlightRef.current.has(key)) return;
      inFlightRef.current.add(key);
      try {
        await onToggle(key);
      } finally {
        inFlightRef.current.delete(key);
      }
    },
    [currentUserId, onToggle]
  );

  const repositionPicker = useCallback(() => {
    const btn = anchorRef.current;
    const panel = pickerPortalRef.current;
    if (!btn || !panel) return;
    const r = btn.getBoundingClientRect();
    const pw = Math.max(panel.offsetWidth, 1);
    const ph = Math.max(panel.offsetHeight, 1);
    const margin = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = r.left;
    let top = r.bottom + 6;
    if (left + pw > vw - margin) left = Math.max(margin, vw - pw - margin);
    if (left < margin) left = margin;
    if (top + ph > vh - margin) {
      top = Math.max(margin, r.top - ph - 6);
    }
    setPickerFixedPos((prev) => (prev && prev.top === top && prev.left === left ? prev : { top, left }));
  }, []);

  useLayoutEffect(() => {
    if (!pickerOpen) {
      setPickerFixedPos(null);
      return;
    }
    repositionPicker();
    const id = window.requestAnimationFrame(() => repositionPicker());
    return () => window.cancelAnimationFrame(id);
  }, [pickerOpen, repositionPicker]);

  useEffect(() => {
    if (!pickerOpen) return;
    const onScrollOrResize = () => repositionPicker();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [pickerOpen, repositionPicker]);

  useEffect(() => {
    if (!pickerOpen) return;
    /** Capture phase so we still see the event if a parent stops propagation on bubble. */
    const close = (e: MouseEvent | PointerEvent) => {
      const el = wrapRef.current;
      const pop = pickerPortalRef.current;
      const target = e.target;
      if (!target || !(target instanceof Node)) return;
      if (el?.contains(target)) return;
      if (pop?.contains(target)) return;
      setPickerOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPickerOpen(false);
    };
    document.addEventListener('mousedown', close, true);
    document.addEventListener('pointerdown', close, true);
    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', close, true);
      document.removeEventListener('pointerdown', close, true);
      window.removeEventListener('keydown', onKeyDown, true);
    };
  }, [pickerOpen]);

  const sortedKeys = Object.keys(reactions).sort();
  const hasReactions = sortedKeys.length > 0;

  /**
   * Select on pointerdown (with preventDefault) so closing the portaled picker cannot
   * let a follow-up click land on the newly rendered reaction pill and undo the toggle —
   * common when the picker flips above the control inside Project Hub comment popouts.
   */
  const handlePickPointerDown = useCallback(
    (e: React.PointerEvent, emoji: string) => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      suppressPillUntilRef.current = Date.now() + POST_PICK_SUPPRESS_MS;
      setPickerOpen(false);
      void runToggle(emoji);
    },
    [runToggle]
  );

  const handlePillToggle = useCallback(
    (emoji: string) => {
      if (Date.now() < suppressPillUntilRef.current) return;
      void runToggle(emoji);
    },
    [runToggle]
  );

  const tooltipFor = (emoji: string, userIds: string[]) => {
    const names = userIds.map((id) => getUserDisplayName(id));
    return `${emoji} ${names.join(', ')}`;
  };

  const renderPill = (emoji: string) => {
    const userIds = reactions[emoji] || [];
    const count = userIds.length;
    const mine = currentUserId ? userIds.includes(currentUserId) : false;
    return (
      <button
        key={emoji}
        type="button"
        className={`${base}-pill ${mine ? `${base}-pill--mine` : ''}`}
        title={tooltipFor(emoji, userIds)}
        aria-label={tooltipFor(emoji, userIds)}
        onClick={() => handlePillToggle(emoji)}
        disabled={!currentUserId}
      >
        <span className={`${base}-emoji`} aria-hidden>
          {emoji}
        </span>
        {count > 1 && <span className={`${base}-count`}>{count}</span>}
      </button>
    );
  };

  if (!currentUserId && sortedKeys.length === 0) {
    return null;
  }

  const hoverClass = revealOnHover ? `${base}--hover` : '';
  const hasClass = hasReactions ? `${base}--has-reactions` : '';
  const pickerClass = pickerOpen ? `${base}--picker-open` : '';

  return (
    <div className={`${base} ${hoverClass} ${hasClass} ${pickerClass}`.trim()} ref={wrapRef}>
      <div className={`${base}-row`} role="group" aria-label="Reactions">
        {!currentUserId &&
          sortedKeys.map((emoji) => {
            const userIds = reactions[emoji] || [];
            return (
              <span key={emoji} className={`${base}-pill ${base}-pill--readonly`} title={tooltipFor(emoji, userIds)}>
                <span className={`${base}-emoji`} aria-hidden>
                  {emoji}
                </span>
                {userIds.length > 1 && <span className={`${base}-count`}>{userIds.length}</span>}
              </span>
            );
          })}

        {currentUserId && (
          <>
            {sortedKeys.map((emoji) => renderPill(emoji))}

            <div className={`${base}-add-wrap`}>
              <button
                ref={anchorRef}
                type="button"
                className={`${base}-add ${hasReactions ? `${base}-add--compact` : ''}`}
                aria-expanded={pickerOpen}
                aria-haspopup="dialog"
                title="Add reaction"
                aria-label="Add reaction"
                onClick={() => setPickerOpen((o) => !o)}
              >
                <SmilePlus size={hasReactions ? 14 : 15} strokeWidth={2} aria-hidden />
                {!hasReactions && <span className={`${base}-add-label`}>Add reaction</span>}
              </button>
              {pickerOpen &&
                createPortal(
                  <div
                    ref={pickerPortalRef}
                    className={`${base}-picker ${base}-picker--portal`}
                    role="dialog"
                    aria-label="Choose emoji"
                    style={
                      pickerFixedPos
                        ? { top: pickerFixedPos.top, left: pickerFixedPos.left, visibility: 'visible' as const }
                        : { top: -9999, left: 0, visibility: 'hidden' as const }
                    }
                  >
                    <div className={`${base}-picker-grid`}>
                      {PICKER_EMOJIS.map((e) => (
                        <button
                          key={e}
                          type="button"
                          className={`${base}-picker-item`}
                          onPointerDown={(ev) => handlePickPointerDown(ev, e)}
                        >
                          {e}
                        </button>
                      ))}
                    </div>
                  </div>,
                  document.body,
                )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CommentReactions;
